import React, { useMemo, useCallback, useState, ChangeEvent, useRef, useEffect } from 'react';
import { Box, Flex, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import partial from 'lodash-es/partial';
import {
  FilterSelectionType,
  useUpdateChannelFilterSelectionMutation,
  ChannelFilterSelectionFragment,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ChannelListFilterDraft,
  ChannelListFilterDraftValue,
  ChannelListFilterDraftValueOption,
} from '../types/ChannelListFilterDraft';
import ChannelListFilterRow from './ChannelListFilterRow';
import { ChannelListFilterSelectionDraft } from '../types/ChannelListFilterSelectionDraft';
import { convertDraftFilterToPersisted } from '../utils/convertDraftFilterToPersisted';
import { IFilterPopoverEditState } from '../interfaces/IFilterPopoverEditState';
import useChannelFilterOptions from '../../../../hooks/useChannelFilterOptions';
import { IFilterOption } from '../interfaces/IFilterOption';
import { ChannelListFilterRowLabel } from '../types/ChannelListFilterRowLabel';
import { ChannelListFilterKey } from 'pages/Conversations/interfaces/IChannelListFilter';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IChannelListFilterEditSavedProps {
  filterPopoverEditState: IFilterPopoverEditState;
  onFilterDelete: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onFilterEditCancel: () => void;
  onFilterUpdate: (channelFilterSelection: ChannelFilterSelectionFragment) => void;
  onSetFilterPopoverEditState: React.Dispatch<React.SetStateAction<IFilterPopoverEditState>>;
  onShowArchivedToggle: () => void;
}

const ChannelListFilterEditSaved = ({
  filterPopoverEditState,
  onFilterDelete,
  onFilterEditCancel,
  onFilterUpdate,
  onSetFilterPopoverEditState,
  onShowArchivedToggle,
}: IChannelListFilterEditSavedProps): JSX.Element => {
  const { clinicUser } = useClinicUser();
  const { filterDraft, channelFilterSelection } = filterPopoverEditState;
  const persistedChannelFilter = useMemo(() => convertDraftFilterToPersisted(filterDraft), [filterDraft]);
  const { trackEvent } = useTracking();

  /**
   * Navigate back to the "Select" view if returning to this popover after reloading the page
   * while on the the "EditSaved" view
   */
  useEffect(() => {
    if (!channelFilterSelection?.id) {
      onFilterEditCancel();
    }
  }, [channelFilterSelection, onFilterEditCancel]);

  const {
    channelStatusOptions,
    clinicUserOptions,
    clinicTagOptions,
    clinicEmployeeOptions,
    appointmentTypeOptions,
    clinicRoomOptions,
  } = useChannelFilterOptions();

  const getSelectedOptions = useCallback(
    (key: ChannelListFilterKey, filter: ChannelListFilterDraft, options: IFilterOption[]) => {
      const value = filter[key];
      if (!Array.isArray(value)) {
        return [];
      }
      return (
        (value
          .map((selectedOption) => {
            const option = options.find(({ value }) => value === selectedOption.value);
            if (!option) return;
            return option;
          })
          .filter((v) => !!v) as ChannelListFilterDraftValueOption[]) || []
      );
    },
    [],
  );

  const onSetFilter = useCallback(
    (filters: ChannelListFilterDraft) => {
      onSetFilterPopoverEditState((currentState) => ({
        ...currentState,
        filterDraft: {
          ...currentState.filterDraft,
          filters,
        },
      }));
    },
    [onSetFilterPopoverEditState],
  );

  const selectedChannelStatusOptions = useMemo(
    () =>
      channelStatusOptions.reduce((prev, curr) => {
        prev.push(
          ...getSelectedOptions(
            'channelStatusIds',
            filterDraft.filters,
            curr.value as ChannelListFilterDraftValueOption[],
          ),
        );
        return prev;
      }, [] as ChannelListFilterDraftValueOption[]),
    [filterDraft, channelStatusOptions, getSelectedOptions],
  );
  const selectedClinicUserOptions = useMemo(
    () =>
      clinicUserOptions.reduce((prev, curr) => {
        prev.push(
          ...getSelectedOptions('assigneeIds', filterDraft.filters, curr.value as ChannelListFilterDraftValueOption[]),
        );
        return prev;
      }, [] as ChannelListFilterDraftValueOption[]),
    [filterDraft, clinicUserOptions, getSelectedOptions],
  );
  const selectedChannelTagOptions = useMemo(
    () => getSelectedOptions('tagNames', filterDraft.filters, clinicTagOptions),
    [filterDraft, clinicTagOptions, getSelectedOptions],
  );
  const selectedClinicEmployeeOptions = useMemo(
    () => getSelectedOptions('appointmentClinicEmployeeIds', filterDraft.filters, clinicEmployeeOptions),
    [filterDraft, clinicEmployeeOptions, getSelectedOptions],
  );
  const selectedAppointmentTypeOptions = useMemo(
    () => getSelectedOptions('appointmentTypeIds', filterDraft.filters, appointmentTypeOptions),
    [filterDraft, appointmentTypeOptions, getSelectedOptions],
  );
  const selectedClinicRoomOptions = useMemo(
    () => getSelectedOptions('appointmentRoomIds', filterDraft.filters, clinicRoomOptions),
    [filterDraft, clinicRoomOptions, getSelectedOptions],
  );

  const handleValueChange = useCallback(
    (key: ChannelListFilterKey, newValue: ChannelListFilterDraftValue): void => {
      const newFilter = {
        ...filterDraft.filters,
        [key]: newValue,
      };
      onSetFilter(newFilter);
    },
    [filterDraft.filters, onSetFilter],
  );

  const [updateChannelFilterSelection, { loading: isUpdatingChannelFilterSelection }] =
    useUpdateChannelFilterSelectionMutation();

  const handleDeleteClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (channelFilterSelection) {
        onFilterDelete(channelFilterSelection);
      }
    },
    [channelFilterSelection, onFilterDelete],
  );
  const [nameInputValidationMessage, setNameInputValidationMessage] = useState('');
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const handleSaveClick = useCallback(async () => {
    const { id, name, filterSelectionType } = filterDraft;
    const { filters } = convertDraftFilterToPersisted(filterDraft);

    if (!id) {
      return;
    }

    if (!name) {
      setNameInputValidationMessage('Please provide a name for this filter');
      nameInputRef.current?.focus();
      return;
    }

    trackEvent(ConversationEvents.FilterDraftSaved);
    const result = await updateChannelFilterSelection({
      variables: {
        data: {
          id,
          name,
          filters,
          filterSelectionType,
        },
      },
    });
    if (result.data?.updateChannelFilterSelection) {
      onFilterUpdate(result.data.updateChannelFilterSelection);
    }
  }, [filterDraft, trackEvent, updateChannelFilterSelection, onFilterUpdate]);

  const handleFilterSelectionValueChange = (key: keyof ChannelListFilterSelectionDraft, value: unknown): void => {
    if (key === 'filterSelectionType') {
      trackEvent(ConversationEvents.FilterShared);
    }
    onSetFilterPopoverEditState((currentState) => ({
      ...currentState,
      filterDraft: {
        ...currentState.filterDraft,
        [key]: value,
      },
    }));
  };

  const filterOwner = useMemo(
    () => channelFilterSelection?.userChannelFilterSelections?.find(({ isFilterOwner }) => isFilterOwner),
    [channelFilterSelection],
  );

  const isFilterReadOnly = useMemo(
    () => !!filterOwner?.user.id && filterOwner.user.id !== clinicUser?.id,
    [filterOwner, clinicUser],
  );

  const isFilterShared = useMemo(
    () => filterDraft.filterSelectionType === FilterSelectionType.PublicReadonly,
    [filterDraft],
  );

  const isFilterDirty = useMemo(() => {
    return (
      !persistedChannelFilter?.id || JSON.stringify(persistedChannelFilter) !== JSON.stringify(channelFilterSelection)
    );
  }, [persistedChannelFilter, channelFilterSelection]);

  const isChangingFilterVisibility = useMemo(
    () =>
      channelFilterSelection?.id &&
      channelFilterSelection?.filterSelectionType !== persistedChannelFilter.filterSelectionType,
    [persistedChannelFilter, channelFilterSelection],
  );

  const showArchived = !!(filterDraft.filters?.showArchived as boolean | undefined);
  const handleShowArchivedToggle = useCallback(
    (source: string) => {
      const newValue = !showArchived;
      trackEvent(ConversationEvents.FilterShowArchivedToggled, { value: newValue, source });
      onShowArchivedToggle();
    },
    [showArchived, trackEvent, onShowArchivedToggle],
  );

  return (
    <Flex data-testid="channel-list-filter-popover-content" direction="column" overflow="hidden">
      <Box flex="0 0 auto" p={3} borderBottom="1px" borderBottomColor="border.default">
        <HStack justify="space-between" pl={1}>
          <HStack>
            <Button
              iconName="chevronLeft"
              variant="ghostNeutral"
              size="md"
              aria-label="Go back"
              onClick={onFilterEditCancel}
            />
            <Heading size="md">{`${isFilterReadOnly ? 'View' : 'Edit'} Saved Filter`}</Heading>
          </HStack>
        </HStack>
      </Box>
      <Box flex="1 1 auto" overflow="auto">
        <VStack
          spacing="4"
          align="stretch"
          py={3}
          px={4}
          borderBottomColor="border.default"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
        >
          <Flex align="center" justify="space-between">
            <HStack flex="1 1 auto">
              {isFilterReadOnly ? (
                <Flex justify="space-between">
                  <Text fontSize="18px" fontWeight="bold" lineHeight="1.2">
                    {filterDraft.name}
                  </Text>
                  <Flex align="center" flex="0 0 auto" ml="4">
                    <Icon name="team" size="md" variant="subtle" />
                    <Text ml="1" size="sm" fontWeight="bold" variant="subtle">
                      {[filterOwner?.user.firstName, filterOwner?.user.lastName].join(' ').trim()}
                    </Text>
                  </Flex>
                </Flex>
              ) : (
                <TextInput
                  autoFocus
                  isRequired
                  label="Filter Name"
                  labelStyle={{ fontWeight: 'semibold' }}
                  ref={nameInputRef}
                  isInvalid={!!nameInputValidationMessage}
                  placeholder="My Filter"
                  value={filterDraft.name}
                  isDisabled={isFilterReadOnly}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    if (e.target.value) {
                      setNameInputValidationMessage('');
                    }
                    partial(handleFilterSelectionValueChange, 'name')(e.target.value);
                  }}
                />
              )}
            </HStack>
          </Flex>
          {!isFilterReadOnly && (
            <Flex direction="column">
              <Switch
                id="channel-list-filter-edit-share"
                label={
                  <Flex alignItems="center">
                    <Icon name="team" size="md" variant="subtle" />
                    <Text ml="1" fontWeight="bold">
                      Share with team?
                    </Text>
                  </Flex>
                }
                isChecked={isFilterShared}
                isDisabled={isFilterReadOnly}
                onChange={(): void =>
                  partial(
                    handleFilterSelectionValueChange,
                    'filterSelectionType',
                  )(isFilterShared ? FilterSelectionType.Private : FilterSelectionType.PublicReadonly)
                }
                formControlStyle={{ width: 'auto' }}
                isFieldInline={true}
              />

              {isChangingFilterVisibility && (
                <Alert
                  status="warning"
                  mt="3"
                  hideCloseButton={true}
                  description={`Heads up! Saving this filter will
                  ${
                    filterDraft?.filterSelectionType === FilterSelectionType.PublicReadonly
                      ? ` allow all team members to view and use (but not edit) this filter.`
                      : ' remove it as an option for all team members.'
                  }`}
                />
              )}
            </Flex>
          )}
        </VStack>
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationStatus}
          selectedOptions={selectedChannelStatusOptions}
          options={channelStatusOptions}
          isReadOnly={isFilterReadOnly}
          showArchived={showArchived}
          onShowArchivedToggle={partial(handleShowArchivedToggle, 'conversations saved filter popover status select')}
          onValueChange={partial(handleValueChange, 'channelStatusIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationAssignees}
          selectedOptions={selectedClinicUserOptions}
          showSelectAllOption={false}
          options={clinicUserOptions}
          isReadOnly={isFilterReadOnly}
          onValueChange={partial(handleValueChange, 'assigneeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.ConversationTags}
          selectedOptions={selectedChannelTagOptions}
          options={clinicTagOptions}
          isReadOnly={isFilterReadOnly}
          onValueChange={partial(handleValueChange, 'tagNames')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentProviders}
          selectedOptions={selectedClinicEmployeeOptions}
          options={clinicEmployeeOptions}
          isReadOnly={isFilterReadOnly}
          onValueChange={partial(handleValueChange, 'appointmentClinicEmployeeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentTypes}
          selectedOptions={selectedAppointmentTypeOptions}
          options={appointmentTypeOptions}
          isReadOnly={isFilterReadOnly}
          onValueChange={partial(handleValueChange, 'appointmentTypeIds')}
        />
        <ChannelListFilterRow
          label={ChannelListFilterRowLabel.AppointmentRooms}
          selectedOptions={selectedClinicRoomOptions}
          options={clinicRoomOptions}
          isReadOnly={isFilterReadOnly}
          isLastRow={true}
          onValueChange={partial(handleValueChange, 'appointmentRoomIds')}
        />
      </Box>
      <Box borderTopColor="border.default" borderTopStyle="solid" borderTopWidth="1px" flex="0 0 auto" py={3} px={4}>
        <Flex justify="flex-start" align="center" pb={3}>
          <HStack as="label" cursor="pointer">
            <Box>
              <Switch
                isDisabled={isFilterReadOnly}
                isChecked={showArchived}
                onChange={partial(handleShowArchivedToggle, 'conversations saved filter popover footer')}
              />
            </Box>
            <Text size="sm">Show Archived</Text>
          </HStack>
        </Flex>
        <Flex align="center" justify="space-between">
          <Button size="sm" variant="tertiary" onClick={onFilterEditCancel}>
            Cancel
          </Button>
          {!isFilterReadOnly && (
            <HStack>
              <Button
                size="sm"
                variant="primaryDestructive"
                minWidth="72px"
                isDisabled={isFilterReadOnly}
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
              <Button
                size="sm"
                variant="primary"
                ml="2"
                isDisabled={!isFilterDirty || isFilterReadOnly}
                isLoading={isUpdatingChannelFilterSelection}
                onClick={handleSaveClick}
              >
                Save Changes
              </Button>
            </HStack>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

export default ChannelListFilterEditSaved;
